import '@/i18n/i18n';
import '@/App.css';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Suspense } from 'react';
import { Box, CircularProgress, CssBaseline } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import InitColorSchemeScript from '@mui/material/InitColorSchemeScript';
import { RouterProvider } from 'react-router';
import { router } from '@/router.tsx';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { theme } from '@/theme/theme';
import { HttpError } from '@/api/HttpError.ts';

let isRedirecting = false;

const handleAuthError = (error: unknown) => {
  if (isRedirecting) return;
  if (!(error instanceof HttpError) || error.status !== 401) return;
  isRedirecting = true;
  const returnTo = window.location.pathname + window.location.search;
  window.location.assign(
    `/.auth/login/aad?post_login_redirect_uri=${encodeURIComponent(returnTo)}`,
  );
};

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: handleAuthError }),
  mutationCache: new MutationCache({ onError: handleAuthError }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60,
      retry: (failureCount, error) => {
        if (error instanceof HttpError && error.status === 401) return false;
        return failureCount < 3;
      },
    },
  },
});

export const App = () => {
  return (
    <>
      <InitColorSchemeScript attribute="data" />
      <Suspense
        fallback={
          <Box
            sx={{
              display: 'flex',
              height: '100vh',
              width: '100vw',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress size="xl" />
          </Box>
        }
      >
        <QueryClientProvider client={queryClient}>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <RouterProvider router={router} />
          </ThemeProvider>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </Suspense>
    </>
  );
};
